// Taşeron portalı — parola sıfırlama 1. adım. E-posta/telefon → `/auth/forgot-
// password` (enumeration-safe: her zaman 200; api-contract §5.8.1). Başarıda 2.
// adıma (kod + yeni parola) hedefi taşıyarak yönlenir.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
  forgotPassword,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const schema = useMemo(
    () => z.object({ emailOrPhone: z.string().min(1, t('validation.identifierRequired')) }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { emailOrPhone: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) => forgotPassword(values),
    onSuccess: (_data, values) =>
      navigate('/reset-password', { state: { emailOrPhone: values.emailOrPhone } }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle as="h1">{t('forgot.title')}</CardTitle>
        <CardDescription className="carbon:mb-6">{t('forgot.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={handleSubmit((values) => mutation.mutate(values))}
          noValidate
        >
          {mutation.isError ? (
            <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
          ) : null}

          <Field
            htmlFor="emailOrPhone"
            label={t('forgot.identifier')}
            error={errors.emailOrPhone?.message}
            required
          >
            <Input
              id="emailOrPhone"
              autoComplete="username"
              invalid={Boolean(errors.emailOrPhone)}
              {...register('emailOrPhone')}
            />
          </Field>

          <Button type="submit" loading={mutation.isPending} className="mt-1 w-full">
            {t('forgot.submit')}
          </Button>

          <div className="text-center text-sm">
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              {t('forgot.backToLogin')}
            </Link>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
